import { apiSlice } from './apiSlice';
import {
  Property,
  PropertyObjectType,
  PlantingAttributeValue,
  PlantingEventPayload,
  PlantingEventResponse,
  PlantingEventsHistoryResponse,
  CropPropertyBinding,
  CreateCropPropertyBindingPayload,
  UpdateCropPropertyBindingPayload,
  EventQueueResponse,
  EventQueueStatus,
  EventQueueType,
  EventDecisionPayload,
  EventDecisionResponse,
  CompleteEventPayload,
  CompleteEventResponse,
  PlantingEvent,
  BatchChildrenResponse,
  adaptProperty,
  adaptPlantingAttributeValue,
} from '../types/plantingEvents';
import {
  CreateEventFromTemplatePayload,
  CreateEventFromTemplateResponse,
  AssignEventPayload,
  AssignEventResponse,
  UnassignEventPayload,
  UnassignEventResponse,
} from '../types/eventTemplates';
import type { WorldRuntimePlanting } from '../types/saves';
import { loadSaveWorldSnapshot, toQueryFnError } from './saveWorldQuery';
import { baseQueryWithReauth } from './baseQueryWithReauth';
import i18n from '../i18n';

const attributesFromRuntime = (plantingId: string, runtime?: WorldRuntimePlanting): PlantingAttributeValue[] => {
  const attributes = [];
  if (typeof runtime?.healthScore === 'number') {
    attributes.push({
      plantingId,
      propertyId: 'health_score',
      propertyCode: 'health_score',
      propertyName: i18n.t('events:runtimeAttributes.healthScore'),
      dataType: 'number',
      valueNumber: runtime.healthScore,
      unit: '%',
    });
  }
  if (typeof runtime?.moisturePct === 'number') {
    attributes.push({
      plantingId,
      propertyId: 'moisture_pct',
      propertyCode: 'moisture_pct',
      propertyName: i18n.t('events:runtimeAttributes.moisture'),
      dataType: 'number',
      valueNumber: runtime.moisturePct,
      unit: '%',
    });
  }
  if (runtime?.vegetationStage) {
    attributes.push({
      plantingId,
      propertyId: 'vegetation_stage',
      propertyCode: 'vegetation_stage',
      propertyName: i18n.t('events:runtimeAttributes.vegetationStage'),
      dataType: 'text',
      valueText: runtime.vegetationStage,
    });
  }
  if (runtime?.lifecyclePhase) {
    attributes.push({
      plantingId,
      propertyId: 'lifecycle_phase',
      propertyCode: 'lifecycle_phase',
      propertyName: i18n.t('events:runtimeAttributes.lifecyclePhase'),
      dataType: 'text',
      valueText: runtime.lifecyclePhase,
    });
  }
  if (typeof runtime?.lifecycleGateProgressPct === 'number') {
    attributes.push({
      plantingId,
      propertyId: 'lifecycle_gate_progress_pct',
      propertyCode: 'lifecycle_gate_progress_pct',
      propertyName: i18n.t('events:runtimeAttributes.nextPhaseProgress'),
      dataType: 'number',
      valueNumber: runtime.lifecycleGateProgressPct,
      unit: '%',
    });
  }
  if (runtime?.lifecycleNextPhase) {
    attributes.push({
      plantingId,
      propertyId: 'lifecycle_next_phase',
      propertyCode: 'lifecycle_next_phase',
      propertyName: i18n.t('events:runtimeAttributes.nextLifecyclePhase'),
      dataType: 'text',
      valueText: runtime.lifecycleNextPhase,
    });
  }
  return attributes.map((attr) => adaptPlantingAttributeValue(attr));
};

export const plantingEventsApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Получить список доступных свойств для измерения
     */
    getProperties: build.query<Property[], PropertyObjectType>({
      query: (objectType) => ({
        url: `/properties`,
        params: { objectType },
      }),
      transformResponse: (response: any) => {
        console.log('getProperties RAW response:', response);
        const properties = response?.properties ?? [];
        console.log('getProperties properties:', properties);
        return Array.isArray(properties)
          ? properties.map((p: any) => adaptProperty(p))
          : [];
      },
    }),

    /**
     * Получить текущие значения свойств посадки
     */
    getPlantingProperties: build.query<PlantingAttributeValue[], { saveId: string; plantingId: string; simSessionId?: string | null }>({
      queryFn: async ({ saveId, plantingId }, api) => {
        try {
          const world = await loadSaveWorldSnapshot(api, saveId);
          return {
            data: attributesFromRuntime(plantingId, world.runtime?.plantings?.[String(plantingId)]),
          };
        } catch (error) {
          return { error: toQueryFnError(error) };
        }
      },
      providesTags: (result, error, { saveId, plantingId }) => [
        { type: 'PlantingAttribute' as const, id: plantingId },
        { type: 'SaveV2' as const, id: `world-${saveId}` },
      ],
      // Перезапрашивать при каждом монтировании для свежих данных
      keepUnusedDataFor: 0,
    }),

    /**
     * Создать событие посадки с измерениями атрибутов
     */
    createPlantingEvent: build.mutation<
      PlantingEventResponse,
      { saveId: string; plantingId: string; payload: PlantingEventPayload; simSessionIdForInvalidation?: string | null }
    >({
      query: ({ saveId, plantingId, payload }) => ({
        url: `/saves/${saveId}/plantings/${plantingId}/events`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: any) => ({
        success: response?.success ?? true,
        event: {
          id: response?.event?.id,
          kind: response?.event?.kind ?? null,
          measuredPropertiesUpdated: response?.event?.measuredPropertiesUpdated ?? 0,
        },
        transplant: response?.transplant ?? null,
      }),
      invalidatesTags: (result, error, { saveId, plantingId, payload, simSessionIdForInvalidation }) => {
        const tags: any[] = [
          { type: 'PlantingAttribute' as const, id: plantingId },
          { type: 'Planting' as const, id: plantingId },
          { type: 'PlantingEvent' as const, id: plantingId },
          // Retro-events trigger a session-wide replay that rewrites
          // current_property_values for every affected planting. The world
          // snapshot reads CPV via current_attributes, so we must invalidate it
          // to keep "Свойства" and runtime-derived fields in sync.
          { type: 'SaveV2' as const, id: `world-${saveId}` },
        ];
        const sid = simSessionIdForInvalidation || (payload as any)?.simSessionId;
        if (sid) {
          // SimTimeseries is registered via simulationApi.enhanceEndpoints;
          // cast to any to bypass the base apiSlice union which doesn't include it.
          tags.push({ type: 'SimTimeseries' as any, id: `session:${sid}` });
          tags.push({ type: 'SimTimeseries' as any, id: `session:${sid}:planting:${plantingId}` });
        }
        return tags;
      },
    }),

    /**
     * Получить историю событий посадки (lazy — вызывается по запросу)
     * Поддерживает фильтры: kind (measurement|service), propertyCode, status
     */
    getPlantingEvents: build.query<
      PlantingEvent[],
      { saveId: string; plantingId: string; kind?: string; propertyCode?: string; status?: string }
    >({
      queryFn: async ({ saveId, plantingId, kind, propertyCode, status }, api, extraOptions) => {
        try {
          const world = await loadSaveWorldSnapshot(api, saveId);

          if (world.type === 'free') {
            const response = await baseQueryWithReauth({
              url: `/plantings/${plantingId}/events`,
              params: {
                ...(kind ? { kind } : {}),
                ...(propertyCode ? { propertyCode } : {}),
                ...(status ? { status } : {}),
              },
            }, api, extraOptions);

            if (response.error) {
              return { error: response.error };
            }

            const payload = response.data as PlantingEventsHistoryResponse | undefined;
            return { data: Array.isArray(payload?.events) ? payload.events : [] };
          }

          const runtime = world.runtime?.plantings?.[String(plantingId)];
          const data = (runtime?.appliedEvents || []).map((event) => ({
            id: String(event.id || `${plantingId}-${event.sequenceNumber || event.date || 'event'}`),
            plantingId,
            type: String(event.action || 'event'),
            kind: String(event.action || 'event'),
            status: 'completed',
            plannedDate: event.date || undefined,
            actualDate: event.date || null,
            properties: [],
          } as unknown as PlantingEvent));
          return { data };
        } catch (error) {
          return { error: toQueryFnError(error) };
        }
      },
      providesTags: (result, error, { saveId, plantingId }) => [
        { type: 'PlantingEvent' as const, id: plantingId },
        { type: 'SaveV2' as const, id: `world-${saveId}` },
      ],
    }),

    /**
     * Получить историю событий грядки (включая историю посадок на этой грядке)
     */
    getBedEvents: build.query<
      PlantingEvent[],
      { bedId: string; kind?: string; propertyCode?: string; status?: string }
    >({
      query: ({ bedId, kind, propertyCode, status }) => ({
        url: `/bed/${bedId}/events`,
        params: {
          ...(kind && { kind }),
          ...(propertyCode && { propertyCode }),
          ...(status && { status }),
        },
      }),
      transformResponse: (response: any): PlantingEvent[] => {
        const events = response?.events ?? [];
        return Array.isArray(events) ? events : [];
      },
      providesTags: (result, error, { bedId }) => [
        { type: 'PlantingEvent' as const, id: `bed-${bedId}` },
      ],
    }),

    /**
     * Получить привязки свойств к культуре
     */
    getCropPropertyBindings: build.query<CropPropertyBinding[], { cropId: string | number; farmId?: string }>({
      query: ({ cropId, farmId }) => ({
        url: `/crops/${cropId}/properties`,
        params: farmId ? { farmId } : undefined,
      }),
      transformResponse: (response: any) => {
        console.log('getCropPropertyBindings RAW response:', response);
        const bindings = response?.bindings ?? [];
        console.log('getCropPropertyBindings bindings:', bindings);
        return Array.isArray(bindings) ? bindings : [];
      },
      providesTags: (result, error, { cropId }) => [
        { type: 'CropPropertyBinding' as const, id: cropId },
      ],
    }),

    /**
     * Создать привязку свойства к культуре
     */
    createCropPropertyBinding: build.mutation<
      CropPropertyBinding,
      { cropId: string | number; payload: CreateCropPropertyBindingPayload }
    >({
      query: ({ cropId, payload }) => ({
        url: `/crops/${cropId}/properties`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, { cropId }) => [
        { type: 'CropPropertyBinding' as const, id: cropId },
      ],
    }),

    /**
     * Обновить привязку свойства к культуре
     */
    updateCropPropertyBinding: build.mutation<
      CropPropertyBinding,
      { cropId: string | number; bindingId: number; payload: UpdateCropPropertyBindingPayload }
    >({
      query: ({ cropId, bindingId, payload }) => ({
        url: `/crops/${cropId}/properties/${bindingId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { cropId }) => [
        { type: 'CropPropertyBinding' as const, id: cropId },
      ],
    }),

    /**
     * Удалить привязку свойства от культуры
     */
    deleteCropPropertyBinding: build.mutation<
      void,
      { cropId: string | number; bindingId: number }
    >({
      query: ({ cropId, bindingId }) => ({
        url: `/crops/${cropId}/properties/${bindingId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { cropId }) => [
        { type: 'CropPropertyBinding' as const, id: cropId },
      ],
    }),

    /**
     * Получить очередь событий (unified endpoint с фильтром assignee)
     * @param assignee - 'all' | 'me' | 'unassigned'
     */
    getEventsQueue: build.query<
      EventQueueResponse,
      { saveId: string; status?: EventQueueStatus; type?: EventQueueType; assignee?: 'all' | 'me' | 'unassigned'; decisionState?: 'proposed' | 'confirmed' | 'rejected' | 'projected' }
    >({
      query: ({ saveId, status, type, assignee = 'all', decisionState }) => ({
        url: `/saves/${saveId}/events/queue`,
        params: {
          assignee,
          ...(status && { status }),
          ...(type && { type }),
          ...(decisionState && { decisionState }),
        },
      }),
      transformResponse: (response: any): EventQueueResponse => ({
        total: response?.total ?? 0,
        completed: response?.completed ?? 0,
        pending: response?.pending ?? 0,
        progress: response?.progress ?? 0,
        byStatus: response?.byStatus,
        events: (response?.events ?? []).map((event: any) => ({
          ...event,
          id: String(event?.id ?? ''),
          parentEventId: event?.parentEventId ? String(event.parentEventId) : null,
          // Нормализуем snake_case в camelCase для полей посадки (CN-0007)
          plantingType: event.plantingType ?? event.planting_type,
          plantingQuantity: event.plantingQuantity ?? event.planting_quantity,
          quantityAffected: event.quantityAffected ?? event.quantity_affected,
          sourceType: event.sourceType ?? event.source_type ?? 'user',
          sourceRef: event.sourceRef ?? event.source_ref ?? null,
          sourceConfidence: event.sourceConfidence ?? event.source_confidence ?? null,
          decisionState: event.decisionState ?? event.decision_state ?? 'confirmed',
          decisionNote: event.decisionNote ?? event.decision_note ?? null,
          decisionBy: event.decisionBy ?? event.decision_by ?? null,
          decisionAt: event.decisionAt ?? event.decision_at ?? null,
        })),
      }),
      providesTags: (result, error, { saveId, assignee }) => [
        { type: 'EventQueue' as const, id: `save-${saveId}-${assignee || 'all'}` },
        // Also honour the shared LIST tag so confirm/reject (which invalidate LIST)
        // refetch the queue — including the "awaiting decision" view.
        { type: 'EventQueue' as const, id: 'LIST' },
      ],
    }),

    /**
     * Получить детали конкретного события
     */
    getEventById: build.query<any, { saveId: string; eventId: string }>({
      query: ({ saveId, eventId }) => ({
        url: `/saves/${saveId}/events/${eventId}`,
        method: 'GET',
      }),
      providesTags: (result, error, { eventId }) => [
        { type: 'PlantingEvent' as const, id: eventId },
      ],
    }),

    /**
     * Завершить событие (CN-0007)
     */
    completeEvent: build.mutation<
      CompleteEventResponse,
      { saveId: string; eventId: string; payload?: CompleteEventPayload }
    >({
      query: ({ saveId, eventId, payload = {} }) => ({
        url: `/saves/${saveId}/events/${eventId}/complete`,
        method: 'PATCH',
        body: payload,
      }),
      transformResponse: (response: any): CompleteEventResponse => ({
        event: {
          id: String(response?.event?.id ?? ''),
          status: response?.event?.status ?? 'completed',
          actualDate: response?.event?.actualDate ?? null,
          parentEventId: response?.event?.parentEventId ? String(response.event.parentEventId) : null,
          plantingId: response?.event?.plantingId ?? null,
        },
        updatedProperties: response?.updatedProperties ?? 0,
        partialCompletion: response?.partialCompletion ? {
          quantityAffected: response.partialCompletion.quantityAffected,
          totalQuantity: response.partialCompletion.totalQuantity,
          isFullyCompleted: response.partialCompletion.isFullyCompleted,
        } : undefined,
      }),
      invalidatesTags: (result, error, { saveId, eventId }) => {
        const tags: Array<
          | { type: 'EventQueue'; id: string }
          | { type: 'PlantingEvent'; id: string }
          | { type: 'PlantingAttribute'; id: string }
          | { type: 'Planting'; id: string }
          | { type: 'SaveV2'; id: string }
        > = [
          { type: 'EventQueue' as const, id: 'LIST' },
          // Completing a retro event may invalidate prior days and trigger replay
          // that rewrites planting CPV — refresh world snapshot.
          { type: 'SaveV2' as const, id: `world-${saveId}` },
        ];
        // Если известен plantingId, инвалидируем конкретную посадку
        if (result?.event?.plantingId) {
          tags.push(
            { type: 'PlantingEvent' as const, id: result.event.plantingId },
            { type: 'PlantingAttribute' as const, id: result.event.plantingId },
            { type: 'Planting' as const, id: result.event.plantingId }
          );
        }
        return tags;
      },
    }),

    /**
     * Подтвердить предложенное событие (automation workflow)
     */
    confirmEvent: build.mutation<
      EventDecisionResponse,
      { saveId: string; eventId: string; payload?: EventDecisionPayload }
    >({
      query: ({ saveId, eventId, payload = {} }) => ({
        url: `/saves/${saveId}/events/${eventId}/confirm`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { saveId }) => [
        { type: 'EventQueue' as const, id: 'LIST' },
        { type: 'SaveV2' as const, id: `world-${saveId}` },
      ],
    }),

    /**
     * Отклонить предложенное событие (automation workflow)
     */
    rejectEvent: build.mutation<
      EventDecisionResponse,
      { saveId: string; eventId: string; payload?: EventDecisionPayload }
    >({
      query: ({ saveId, eventId, payload = {} }) => ({
        url: `/saves/${saveId}/events/${eventId}/reject`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { saveId }) => [
        { type: 'EventQueue' as const, id: 'LIST' },
        { type: 'SaveV2' as const, id: `world-${saveId}` },
      ],
    }),

    /**
     * Создать событие(я) из шаблона (CN-0008)
     */
    createEventFromTemplate: build.mutation<
      CreateEventFromTemplateResponse,
      { saveId: string; payload: CreateEventFromTemplatePayload; simSessionIdForInvalidation?: string | null }
    >({
      query: ({ saveId, payload }) => ({
        url: `/saves/${saveId}/events/from-template`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, { saveId, simSessionIdForInvalidation }) => [
        { type: 'EventQueue' as const, id: `save-${saveId}` },
        { type: 'EventQueue' as const, id: 'LIST' },
        { type: 'SaveV2' as const, id: `world-${saveId}` },
        ...(simSessionIdForInvalidation
          ? [{ type: 'SimTimeseries' as any, id: `session:${simSessionIdForInvalidation}` }]
          : []),
      ],
    }),

    /**
     * Назначить событие на пользователя (CN-0008)
     */
    assignEvent: build.mutation<
      AssignEventResponse,
      { saveId: string; eventId: string; payload: AssignEventPayload }
    >({
      query: ({ saveId, eventId, payload }) => ({
        url: `/saves/${saveId}/events/${eventId}/assign`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { saveId }) => [
        { type: 'EventQueue' as const, id: `save-${saveId}` },
        { type: 'EventQueue' as const, id: 'LIST' },
      ],
    }),

    /**
     * Снять назначение события (CN-0008)
     */
    unassignEvent: build.mutation<
      UnassignEventResponse,
      { saveId: string; eventId: string; payload: UnassignEventPayload }
    >({
      query: ({ saveId, eventId, payload }) => ({
        url: `/saves/${saveId}/events/${eventId}/unassign`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { saveId }) => [
        { type: 'EventQueue' as const, id: `save-${saveId}` },
        { type: 'EventQueue' as const, id: 'LIST' },
      ],
    }),

    /**
     * Редактировать событие (изменение статуса, notes, plannedDate, assignedTo)
     */
    updateEvent: build.mutation<
      any,
      { saveId: string; eventId: string; payload: { status?: string; notes?: string; plannedDate?: string; actualDate?: string; assignedTo?: string } }
    >({
      query: ({ saveId, eventId, payload }) => ({
        url: `/saves/${saveId}/events/${eventId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { saveId, eventId }) => [
        { type: 'EventQueue' as const, id: 'LIST' },
        { type: 'PlantingEvent' as const, id: eventId },
        // Editing actual_date or status of an event can move the invalidation
        // boundary backwards and trigger replay; world snapshot must refresh.
        { type: 'SaveV2' as const, id: `world-${saveId}` },
      ],
    }),

    /**
     * Удалить событие из истории
     */
    deleteEvent: build.mutation<void, { saveId: string; eventId: string; plantingId?: string }>({
      query: ({ saveId, eventId }) => ({
        url: `/saves/${saveId}/events/${eventId}`,
        method: 'DELETE',
      }),
      // getPlantingEvents tags itself by plantingId, not eventId — invalidate
      // both so the planting timeline refetches after a soft-delete.
      invalidatesTags: (result, error, { saveId, eventId, plantingId }) => [
        { type: 'EventQueue' as const, id: 'LIST' },
        { type: 'PlantingEvent' as const, id: eventId },
        ...(plantingId ? [{ type: 'PlantingEvent' as const, id: plantingId }] : []),
        // Deleting an event re-runs replay; world snapshot must refresh so
        // current_attributes reflect the post-replay state.
        { type: 'SaveV2' as const, id: `world-${saveId}` },
      ],
    }),

    /**
    /**
     * Получить дочерние события batch родителя (Batch Events)
     */
    getBatchChildren: build.query<
      BatchChildrenResponse,
      { saveId: string; parentEventId: string }
    >({
      query: ({ saveId, parentEventId }) => ({
        url: `/saves/${saveId}/events/${parentEventId}/children`,
      }),
      transformResponse: (response: any): BatchChildrenResponse => ({
        parent: {
          ...response?.parent,
          id: String(response?.parent?.id ?? ''),
          templateId: response?.parent?.templateId ?? 0,
          type: response?.parent?.type ?? '',
          status: response?.parent?.status ?? 'planned',
          batchTotal: response?.parent?.batchTotal ?? 0,
          childrenCount: response?.parent?.childrenCount ?? 0,
          completedCount: response?.parent?.completedCount ?? 0,
          pendingCount: response?.parent?.pendingCount ?? 0,
          progress: response?.parent?.progress ?? 0,
          properties: response?.parent?.properties ?? [],
        },
        children: (response?.children ?? []).map((child: any) => ({
          ...child,
          id: String(child?.id ?? ''),
          parentEventId: child?.parentEventId ? String(child.parentEventId) : null,
        })),
      }),
      providesTags: (result, error, { parentEventId }) => [
        { type: 'EventQueue' as const, id: `batch-${parentEventId}` },
      ],
    }),
  }),
});

export const {
  useGetPropertiesQuery,
  useLazyGetPropertiesQuery,
  useGetPlantingPropertiesQuery,
  useLazyGetPlantingPropertiesQuery,
  useCreatePlantingEventMutation,
  useGetPlantingEventsQuery,
  useLazyGetPlantingEventsQuery,
  useLazyGetBedEventsQuery,
  useGetCropPropertyBindingsQuery,
  useCreateCropPropertyBindingMutation,
  useUpdateCropPropertyBindingMutation,
  useDeleteCropPropertyBindingMutation,
  useGetEventsQueueQuery,
  useLazyGetEventsQueueQuery,
  useGetEventByIdQuery,
  useCompleteEventMutation,
  useConfirmEventMutation,
  useRejectEventMutation,
  useCreateEventFromTemplateMutation,
  useAssignEventMutation,
  useUnassignEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useGetBatchChildrenQuery,
  useLazyGetBatchChildrenQuery,
} = plantingEventsApi;
